import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useQueryClient, QueryClient } from '@tanstack/react-query';
import { homeRoute } from '@/config/homeRoute';
import { useShellIdentity } from '@/components/shell/useShellIdentity';

const segmentLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  patients: 'Patients',
  schedule: 'Schedule',
  records: 'Records',
  'lab-orders': 'Lab Orders',
  'imaging-orders': 'Imaging Orders',
  admin: 'Admin',
  users: 'Users',
  billing: 'Billing',
  drugs: 'Drugs',
  templates: 'Templates',
  prescriptions: 'Prescriptions',
  notifications: 'Notifications',
  new: 'New',
  edit: 'Edit',
  settings: 'Settings',
  'system-settings': 'System Settings',
  'audit-logs': 'Audit Logs',
  'feature-toggles': 'Feature Toggles',
  'provider-availability': 'Provider Availability',
  'waitlist-management': 'Waitlist',
  'resource-scheduling': 'Resource Scheduling',
  roles: 'Roles',
  timeline: 'Timeline',
  refills: 'Refills',
  mar: 'MAR',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function resolveSegmentLabel(segment: string, prevSegment: string | undefined, queryClient: QueryClient): string {
  if (!UUID_RE.test(segment)) {
    return segmentLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
  }

  // Try to resolve UUID from query cache
  if (prevSegment === 'patients') {
    const patientData: unknown = queryClient.getQueryData(['patients', segment]);
    const patient = (patientData as { data?: { firstName?: string; lastName?: string } })?.data || (patientData as { firstName?: string; lastName?: string });
    if (patient?.firstName && patient?.lastName) {
      return `${patient.firstName} ${patient.lastName}`;
    }
  }

  if (prevSegment === 'appointments') {
    return 'Appointment Details';
  }

  if (prevSegment === 'lab-orders') {
    return 'Order Details';
  }

  if (prevSegment === 'imaging-orders') {
    return 'Order Details';
  }

  if (prevSegment === 'prescriptions') {
    return 'Prescription Details';
  }

  if (prevSegment === 'records') {
    return 'Record Details';
  }

  if (prevSegment === 'templates') {
    return 'Template Details';
  }

  return 'Details';
}

export const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  /* The home crumb — the role's own first door under the design shell, not the
     legacy dashboard. Above the early return: hooks run unconditionally. */
  const { surface } = useShellIdentity();

  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Don't render breadcrumbs on dashboard (root) or single-level pages
  if (pathSegments.length <= 1) {
    return null;
  }

  const crumbs = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const prevSegment = index > 0 ? pathSegments[index - 1] : undefined;
    const label = resolveSegmentLabel(segment, prevSegment, queryClient);
    const isLast = index === pathSegments.length - 1;

    return { path, label, isLast };
  });

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1 text-sm mb-4 flex-wrap"
      style={{ color: 'var(--color-text-secondary)' }}
    >
      <Link
        to={homeRoute(surface)}
        className="flex items-center gap-1 hover:opacity-80 transition-opacity"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        <Home className="w-3.5 h-3.5" />
      </Link>

      {crumbs.map((crumb) => (
        <React.Fragment key={crumb.path}>
          <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-tertiary)' }} />
          {crumb.isLast ? (
            <span
              className="font-medium truncate max-w-[200px]"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {crumb.label}
            </span>
          ) : (
            <Link
              to={crumb.path}
              className="hover:opacity-80 transition-opacity truncate max-w-[200px]"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {crumb.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};
